// src/components/block/broadcastingChannelManagment/BroadcastedMediaListV2.tsx

import React, {
  useState, useEffect, useLayoutEffect, useRef, useCallback,
} from 'react';
import classNames from 'classnames';
import ReactDOM from 'react-dom';
import styles from './BroadcastedMediaListV2.module.scss';

import Icon             from '../../widget/images/Icon';

import { convertStringToDate, convertTimeToSeconds, formatDurationInSeconds } from '../../../dataTypes/timeManagment/utils';
import { TaggedEnrichedBroadcastedMedia, EnrichedBroadcastingChannel } from '../../../dataTypes/broadcastingManagment/types';
import { isReorderable, reorderProgram, deleteProgram, restoreProgram, cancelNewProgram,
         loopHolderIndex, isHeldByLoop, setProgramLoop, effectiveEndMs, priorityCover, PriorityCover,
         liveProgramIndex, hasEnded } from '../../../dataTypes/broadcastingManagment/scheduleOperations';
import { getCurrentStreamMetadata } from '../../../backendServices/broadcastingManagment/api_broadcastingChannel';
import { Media, MediaCategory, initializedMedia } from '../../../dataTypes/mediaManagment/types';
import { Video } from '../../../dataTypes/mediaManagment/video_types';
import { AudioSlideshow } from '../../../dataTypes/mediaManagment/audioSlideshow_types';
import { WWW_URLS } from '../../../backendServices/urls';
import MediaSelector from '../mediaManagment/MediaSelector';
import DateTimeSelector from '../../widget/inputs/DateTimeSelector';
import Toggle from '../../widget/inputs/Toggle';
import Select from '../../widget/inputs/Select';
import Window from '../../widget/windows/window';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { getVideoAudioTracks, getVideoPlayability, VideoAudioTrack } from '../../../backendServices/mediaManagment/api_video';

function getDefaultDateTime(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() + 2);
  d.setSeconds(0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
}

function formatDayLabel(date: Date | null): string {
  if (!date) return '';
  try {
    const today    = new Date(); today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
    const d        = new Date(date); d.setHours(0, 0, 0, 0);
    if (d.getTime() === today.getTime())    return `Aujourd'hui — ${format(date, 'EEEE d MMMM yyyy', { locale: fr })}`;
    if (d.getTime() === tomorrow.getTime()) return `Demain — ${format(date, 'EEEE d MMMM yyyy', { locale: fr })}`;
    return format(date, 'EEEE d MMMM yyyy', { locale: fr });
  } catch { return ''; }
}

function formatTime(date: Date | null): string {
  if (!date) return '--:--';
  try { return format(date, 'HH:mm'); } catch { return '--:--'; }
}

// Tuile ordinaire : vignette 64 px + padding vertical 2 × 10 px (cf. .programRow). Sert
// d'échelle à l'espace ajouté en tête d'une tuile qui reprendra après un « Lire maintenant ».
const ROW_PADDING_PX = 10;
const ROW_HEIGHT_PX  = 64 + 2 * ROW_PADDING_PX;

function totalDuration(medias: TaggedEnrichedBroadcastedMedia[]): number {
  return medias.filter(m => m.tag !== 'to delete').reduce((acc, m) => acc + (m.duration || 0), 0);
}

const Icons = {
  Plus:     () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  Play:     () => <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><polygon points="5 3 19 12 5 21 5 3"/></svg>,
  List:     () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>,
  Cal:      () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  ChevronRight: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><polyline points="9 18 15 12 9 6"/></svg>,
  Dots:     () => <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>,
  Grip:     () => <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><circle cx="9" cy="6" r="1.5"/><circle cx="15" cy="6" r="1.5"/><circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/><circle cx="9" cy="18" r="1.5"/><circle cx="15" cy="18" r="1.5"/></svg>,
  Clock:    () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  Eye:      () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  Film:     () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24"><rect x="2" y="2" width="20" height="20" rx="2.18"/><line x1="7" y1="2" x2="7" y2="22"/><line x1="17" y1="2" x2="17" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="2" y1="7" x2="7" y2="7"/><line x1="2" y1="17" x2="7" y2="17"/><line x1="17" y1="17" x2="22" y2="17"/><line x1="17" y1="7" x2="22" y2="7"/></svg>,
  Trash:    () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>,
  Undo:     () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><polyline points="9 14 4 9 9 4"/><path d="M20 20v-7a4 4 0 0 0-4-4H4"/></svg>,
  Up:       () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><polyline points="18 15 12 9 6 15"/></svg>,
  Down:     () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><polyline points="6 9 12 15 18 9"/></svg>,
  Bookmark: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>,
  Warning:  () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  Save:     () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>,
  StatsLine:() => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  Next:     () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><polygon points="5 4 15 12 5 20 5 4"/><line x1="19" y1="5" x2="19" y2="19"/></svg>,
  Lock:     () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
  Shuffle:  () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20"><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/><line x1="4" y1="4" x2="9" y2="9"/></svg>,
  Repeat:   () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>,
};

export interface BroadcastedMediaListV2Props {
  broadcastedMedias          : TaggedEnrichedBroadcastedMedia[];
  onBroadcastedMediasChange  : (v: TaggedEnrichedBroadcastedMedia[]) => void;
  onStatusUpdate?            : () => void;
  pollingInterval?           : number;
  enablePolling?             : boolean;
  mediaSet?                  : Media[];
  onRefreshMedias?           : () => Promise<Media[] | null>;
  mediaCategories?           : MediaCategory[];
  broadcastingChannelId?     : number;
  enrichedBroadcastingChannel?: EnrichedBroadcastingChannel;
  dateAndTimeAfterLastBroadcastedMedia?: string;
  onNewBroadcastedMedia?     : (m: TaggedEnrichedBroadcastedMedia) => void;
  onSaveSchedule?            : () => void;
  buttonToClick?             : boolean;
  onAckAnimationFinished?    : () => void;
  channelActive?             : boolean;
}

type TabFilter = 'today' | 'upcoming' | 'broadcasted';

interface DragState {
  srcIndex  : number;
  clone     : HTMLElement;
  rowHeight : number;
  startX    : number;
  startY    : number;
  offsetX   : number;
  offsetY   : number;
}

export const BroadcastedMediaListV2 = ({
  broadcastedMedias,
  onBroadcastedMediasChange,
  onStatusUpdate,
  pollingInterval   = 15000,
  enablePolling     = true,
  mediaSet          = [],
  onRefreshMedias,
  mediaCategories   = [],
  broadcastingChannelId = 0,
  enrichedBroadcastingChannel,
  dateAndTimeAfterLastBroadcastedMedia = '',
  onNewBroadcastedMedia,
  onSaveSchedule,
  buttonToClick     = false,
  onAckAnimationFinished,
  channelActive     = true,
}: BroadcastedMediaListV2Props) => {

  const [medias,         setMedias]         = useState<TaggedEnrichedBroadcastedMedia[]>(broadcastedMedias);
  const [activeTab,      setActiveTab]      = useState<TabFilter>('today');
  const [openMenuIndex,  setOpenMenuIndex]  = useState<number | null>(null);
  const [bandeauStates,  setBandeauStates]  = useState<Record<number, boolean>>({});
  const [pubStates,      setPubStates]      = useState<Record<number, boolean>>({});

  const dragRef       = useRef<DragState | null>(null);
  const listRef       = useRef<HTMLDivElement>(null);
  const rowRefs       = useRef<(HTMLDivElement | null)[]>([]);
  const menuBtnRefs   = useRef<Map<number, HTMLButtonElement>>(new Map());
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);

  const [dragSrcIdx,  setDragSrcIdx]  = useState(-1);
  const [insertAfter, setInsertAfter] = useState<number>(-2);

  const insertAfterRef    = useRef<number>(-2);
  const srcVisualIndexRef = useRef<number>(-1);
  const filteredMediasRef = useRef<TaggedEnrichedBroadcastedMedia[]>([]);
  const mediasRef         = useRef<TaggedEnrichedBroadcastedMedia[]>(broadcastedMedias);

  const [addMode,         setAddMode]         = useState<'none' | 'queue' | 'date' | 'now'>('none');
  const [mediaSelection,  setMediaSelection]  = useState(false);
  const [choosenMedia,    setChoosenMedia]    = useState<Media>(initializedMedia());
  const [choosenDuration, setChoosenDuration] = useState(0);
  // Pistes audio du média choisi (cf. VideoAudioTracks côté admin-api) — vide tant qu'aucune
  // vidéo à plusieurs pistes n'a été sélectionnée. audioTrackIndex reste null (comportement
  // par défaut : première piste) tant que l'utilisateur ne choisit pas explicitement.
  const [audioTracks,     setAudioTracks]     = useState<VideoAudioTrack[]>([]);
  const [audioTrackIndex, setAudioTrackIndex] = useState<number | null>(null);
  const [dateAndTime,     setDateAndTime]     = useState(getDefaultDateTime());
  const [showConfirmMsg,  setShowConfirmMsg]  = useState<string | null>(null);
  // Dernière vidéo choisie : la réponse tardive du contrôle de lisibilité ne doit pas écraser un choix plus récent.
  const lastSelectedMediaIdRef = useRef<number | null>(null);

  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const menuRef    = useRef<HTMLDivElement>(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  // Lignes sélectionnées, par id de programme (les ajouts non enregistrés ont l'id 0 : non sélectionnables).
  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set());
  const checkAnchorRef   = useRef<number | null>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);

  // Pistes audio des médias déjà programmés, par mediaId — indépendant de audioTracks/
  // audioTrackIndex ci-dessus (qui ne servent qu'au formulaire d'AJOUT). Un média sans
  // ambiguïté (une seule piste) n'a pas d'entrée utile ici : payload vide = pas d'affichage.
  // fetchedTrackIdsRef évite de refetcher un média déjà interrogé (les pistes d'un média déjà
  // transcodé ne changent pas pendant que la page reste ouverte).
  const [audioTracksCache, setAudioTracksCache] = useState<Record<number, VideoAudioTrack[]>>({});
  const fetchedTrackIdsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    const videoMediaIds = new Set(
      medias.filter(m => m.media?.mediaType === 'Video' && m.mediaId > 0).map(m => m.mediaId)
    );
    videoMediaIds.forEach(id => {
      if (fetchedTrackIdsRef.current.has(id)) return;
      fetchedTrackIdsRef.current.add(id);
      getVideoAudioTracks(id).then(response => {
        setAudioTracksCache(prev => ({ ...prev, [id]: response.code === 1 ? response.payload : [] }));
      });
    });
  }, [medias]);

  // Modifie la piste audio d'un programme déjà créé — même règle de tag que les autres
  // modifications de champ (cf. BroadcastedMediaCreation.tsx : 'to create'/'to delete' ne
  // sont jamais écrasés par 'to update', tout le reste le devient pour être renvoyé à la
  // sauvegarde).
  const handleAudioTrackEdit = (globalIndex: number, newIndex: number | null) => {
    setMedias(prev => {
      const updated = prev.map((m, i) => {
        if (i !== globalIndex) return m;
        const nextTag = (m.tag === 'to create' || m.tag === 'to delete') ? m.tag : 'to update';
        return { ...m, audioTrackIndex: newIndex, tag: nextTag };
      });
      onBroadcastedMediasChange(updated);
      return updated;
    });
  };

  useEffect(() => {
    if (!enablePolling || !onStatusUpdate) return;
    pollingRef.current = setInterval(() => onStatusUpdate(), pollingInterval);
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [enablePolling, onStatusUpdate, pollingInterval]);

  // Horloge locale pour la jauge de progression du programme EN COURS — même
  // principe que BroadcastingChannelDashboard : le rafraîchissement des
  // données (pollingInterval) est trop grossier pour une jauge qui doit
  // avancer visiblement. La progression se déduit de broadcastDate +
  // duration, jamais de ce compteur directement.
  const [progressClock, setProgressClock] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setProgressClock(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Départ RÉEL du programme en cours (horloge du navigateur), relevé auprès du microservice :
  // maintenant − elapsed_seconds de schedule/current. Même correctif que le tableau de bord
  // (BroadcastingChannelDashboard, liveStartMs) : une vidéo enchaînée démarre souvent 1 à 3 s
  // avant son broadcastDate, et la jauge calée sur celui-ci était en retard d'autant.
  // Relevé à l'arrivée d'un nouveau programme en cours, puis au rythme de pollingInterval.
  const [liveStart, setLiveStart] = useState<{ bmId: number; startMs: number } | null>(null);
  const liveBmId  = medias.find(m => m.tag === 'current')?.id ?? null;
  const publicRef = enrichedBroadcastingChannel?.publicRef;
  useEffect(() => {
    if (liveBmId === null || !publicRef) { setLiveStart(null); return; }
    let cancelled = false;
    const fetchLiveStart = async () => {
      const current = await getCurrentStreamMetadata(publicRef);
      if (cancelled || !current) return;
      setLiveStart(current.status === 'playing' && typeof current.elapsed_seconds === 'number'
        ? { bmId: current.broadcasted_media_id, startMs: Date.now() - current.elapsed_seconds * 1000 }
        : null);
    };
    fetchLiveStart();
    const id = setInterval(fetchLiveStart, pollingInterval);
    return () => { cancelled = true; clearInterval(id); };
  }, [liveBmId, publicRef, pollingInterval]);

  // Trié par broadcastDate à chaque synchronisation depuis le parent (source externe :
  // chargement initial, merge après mise à jour, reloadCurrentAndNext...) — l'ordre
  // d'affichage ne doit jamais dépendre de l'ordre d'insertion dans le tableau. Les
  // opérations interactives (drag/déplacer, ci-dessous) restent, elles, basées sur
  // l'ordre du tableau ET recalculent les dates en conséquence via scheduleOperations :
  // après un tri par date ici, elles retombent sur un ordre identique (aucun effet de
  // bord). Ce tri chronologique est d'ailleurs une PRÉCONDITION de deleteProgram, qui
  // décale « les programmes suivants » au sens de l'ordre du tableau.
  useEffect(() => {
    const sorted = [...broadcastedMedias].sort((a, b) => {
      const dateA = a.broadcastDate ? new Date(a.broadcastDate).getTime() : Infinity;
      const dateB = b.broadcastDate ? new Date(b.broadcastDate).getTime() : Infinity;
      return dateA - dateB;
    });
    setMedias(sorted);
  }, [broadcastedMedias]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node))
        setOpenMenuIndex(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!actionMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target as Node))
        setActionMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [actionMenuOpen]);

  const openMenu = (globalIndex: number) => {
    const btn = menuBtnRefs.current.get(globalIndex);
    if (btn) {
      const rect = btn.getBoundingClientRect();
      setMenuPosition({ top: rect.bottom + 4, left: rect.right });
    }
    setOpenMenuIndex(prev => prev === globalIndex ? null : globalIndex);
  };

  // Ni la boucle ni ce qu'elle retient ne se réorganisent : leurs heures n'ont pas de sens
  // tant qu'elle tourne. Hors boucle, règle habituelle (isReorderable).
  const canReorder = (list: TaggedEnrichedBroadcastedMedia[], i: number): boolean =>
    isReorderable(list[i]) && !list[i].loop && !isHeldByLoop(list, i);

  const applyLoop = (id: number, index: number, loop: boolean, cycleEndMs?: number) => {
    setMedias(prev => {
      // L'index a pu bouger pendant l'appel au microservice : on retrouve le programme.
      const i = id !== 0 ? prev.findIndex(m => m.id === id) : index;
      if (i < 0) return prev;
      const updated = setProgramLoop(prev, i, loop, cycleEndMs);
      if (updated === prev) return prev;
      onBroadcastedMediasChange(updated);
      return updated;
    });
  };

  // Désarmer la boucle d'un programme EN COURS : les suivants repartent à la fin du tour en
  // cours, que seul le microservice connaît (schedule/current : remaining_seconds est relatif
  // au tour). À défaut de réponse, on l'estime depuis l'heure de départ — enregistrer la
  // corrigera de toute façon (cf. correctLoopRelease dans BroadcastingChannelSheet).
  const handleLoopToggle = async (index: number, loop: boolean) => {
    const m = medias[index];
    if (!m) return;

    if (loop || m.tag !== 'current' || !m.broadcastDate || !(m.duration > 0)) {
      applyLoop(m.id, index, loop);
      return;
    }

    const now       = Date.now();
    const startMs   = new Date(m.broadcastDate).getTime();
    const cycles    = Math.max(1, Math.ceil((now - startMs) / (m.duration * 1000)));
    let   cycleEnd  = startMs + cycles * m.duration * 1000;

    const current   = publicRef ? await getCurrentStreamMetadata(publicRef) : null;
    if (current?.status === 'playing' && current.broadcasted_media_id === m.id && typeof current.remaining_seconds === 'number') {
      cycleEnd = Date.now() + current.remaining_seconds * 1000;
    }

    applyLoop(m.id, index, false, cycleEnd);
  };

  // Le recalcul des heures vit désormais dans scheduleOperations.ts (fonctions pures,
  // testées) : les trous volontaires entre programmes y sont traités comme des
  // pseudo-programmes et survivent aux réorganisations comme aux suppressions.
  // isReorderable y est également défini, pour que l'éligibilité au déplacement et le
  // périmètre du recalcul ne puissent plus diverger.

  const computeInsertAfter = useCallback((clientY: number): number => {
    if (!listRef.current) return -1;
    const rows = rowRefs.current;
    let best = -1;
    for (let i = 0; i < rows.length; i++) {
      const el = rows[i];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      if (clientY > rect.top + rect.height / 2) best = i;
    }
    return best;
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent, globalIndex: number) => {
    if (e.button !== 0) return;
    if (!isReorderable(medias[globalIndex])) return;
    const target = e.target as HTMLElement;
    if (!target.closest('[data-drag-handle]')) return;
    e.preventDefault();
    e.stopPropagation();
    const rowEl = rowRefs.current[globalIndex];
    if (!rowEl) return;
    const rect    = rowEl.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    const clone = rowEl.cloneNode(true) as HTMLElement;
    clone.style.cssText = `
      position:fixed;left:${rect.left}px;top:${rect.top}px;
      width:${rect.width}px;height:${rect.height}px;
      z-index:9999;pointer-events:none;border-radius:10px;
      box-shadow:0 20px 60px rgba(0,0,0,0.55),0 0 0 2px #2e6fe0;
      background:#1c2a40;opacity:1;
      transform:scale(1.03) rotate(-0.6deg);
      transition:transform 0.12s ease,box-shadow 0.12s ease;
      will-change:transform,left,top;
    `;
    document.body.appendChild(clone);
    dragRef.current = { srcIndex: globalIndex, clone, rowHeight: rect.height, startX: e.clientX, startY: e.clientY, offsetX, offsetY };
    setDragSrcIdx(globalIndex);
    setInsertAfter(globalIndex);
    (e.target as Element).setPointerCapture(e.pointerId);
  }, [medias]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    const ds = dragRef.current;
    if (!ds) return;
    ds.clone.style.left = `${e.clientX - ds.offsetX}px`;
    ds.clone.style.top  = `${e.clientY - ds.offsetY}px`;
    setInsertAfter(computeInsertAfter(e.clientY));
  }, [computeInsertAfter]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    const ds = dragRef.current;
    if (!ds) return;
    const srcIdx = ds.srcIndex;
    dragRef.current = null;
    const currentInsertAfter = insertAfterRef.current;
    const sourceRow = rowRefs.current[srcIdx];
    const landingRect = sourceRow ? sourceRow.getBoundingClientRect() : null;
    if (landingRect) {
      ds.clone.style.transition = 'left 0.22s cubic-bezier(0.4,0,0.2,1),top 0.22s cubic-bezier(0.4,0,0.2,1),transform 0.22s cubic-bezier(0.4,0,0.2,1),box-shadow 0.22s ease,opacity 0.08s ease 0.2s';
      ds.clone.style.left      = `${landingRect.left}px`;
      ds.clone.style.top       = `${landingRect.top}px`;
      ds.clone.style.transform = 'scale(1) rotate(0deg)';
      ds.clone.style.boxShadow = 'none';
      ds.clone.style.opacity   = '0';
      setTimeout(() => ds.clone.remove(), 320);
    } else {
      ds.clone.remove();
    }
    setDragSrcIdx(-1);
    setInsertAfter(-2);
    if (currentInsertAfter === -2 || srcIdx < 0) return;
    setMedias(prevMedias => {
      if (srcIdx >= prevMedias.length) return prevMedias;
      if (!canReorder(prevMedias, srcIdx)) return prevMedias;
      const fm = filteredMediasRef.current;
      let destIdx: number;
      if (currentInsertAfter === -1) {
        destIdx = fm.length > 0 ? prevMedias.indexOf(fm[0]) : 0;
      } else {
        // currentInsertAfter est un indice GLOBAL : computeInsertAfter parcourt rowRefs,
        // lui-même indexé par globalIndex. Il était jusqu'ici utilisé comme un indice dans
        // filteredMedias — les deux ne coïncident que si le filtre est un préfixe de la
        // liste (onglet "Aujourd'hui"). Dans l'onglet "À venir" (un suffixe), le dépôt
        // atterrissait sur la mauvaise ligne.
        const afterGlobal = Math.min(currentInsertAfter, prevMedias.length - 1);
        destIdx = afterGlobal > srcIdx ? afterGlobal : afterGlobal + 1;
      }
      // Rien ne se dépose dans la zone retenue par une boucle (ni sur la boucle elle-même).
      const holder = loopHolderIndex(prevMedias);
      if (holder !== -1 && destIdx >= holder) return prevMedias;
      const updated = reorderProgram(prevMedias, srcIdx, destIdx);
      if (updated === prevMedias) return prevMedias;
      onBroadcastedMediasChange(updated);
      return updated;
    });
  }, [onBroadcastedMediasChange]);

  // Les flèches ne font que permuter : déplacer d'un cran suffit, reorderProgram se charge
  // de conserver le trou entre les deux programmes et de laisser intact tout ce qui les
  // entoure (la durée totale du couple est inchangée).
  const handleMove = (index: number, dir: 'up' | 'down') => {
    setMedias(prev => {
      const permutable = prev.filter((_, i) => canReorder(prev, i));
      const pi         = permutable.indexOf(prev[index]);
      if (pi < 0) return prev;
      const target     = dir === 'up' ? permutable[pi - 1] : permutable[pi + 1];
      if (!target) return prev;
      const updated = reorderProgram(prev, index, prev.indexOf(target));
      if (updated === prev) return prev;
      onBroadcastedMediasChange(updated);
      return updated;
    });
  };

  const handleDelete = (index: number) => {
    setMedias(prev => {
      const updated = deleteProgram(prev, index);
      if (updated === prev) return prev;
      onBroadcastedMediasChange(updated);
      return updated;
    });
    setOpenMenuIndex(null);
  };

  // « Tout effacer » : mêmes règles que « Supprimer » ligne par ligne (le programme en cours,
  // les ratés et ce qui est déjà archivé restent), les ajouts non enregistrés sont retirés.
  // De la fin vers le début : cancelNewProgram retire une entrée, les index d'avant restent valides.
  const handleClearAll = () => {
    setMedias(prev => {
      let updated = prev;
      for (let i = prev.length - 1; i >= 0; i--) {
        const tag = prev[i].tag;
        if (tag === 'to create') updated = cancelNewProgram(updated, i);
        else if (!['to delete','to archive','archived','current','missed','being missed','not found'].includes(tag)) updated = deleteProgram(updated, i);
      }
      if (updated === prev) return prev;
      onBroadcastedMediasChange(updated);
      return updated;
    });
    setActionMenuOpen(false);
  };

  // « Effacer la sélection » : mêmes règles que « Supprimer » ; les lignes restantes
  // (en cours, archivées...) ne sont plus cochables, la sélection est donc vidée.
  const handleClearChecked = () => {
    setMedias(prev => {
      let updated = prev;
      for (let i = prev.length - 1; i >= 0; i--) {
        const m = prev[i];
        if (m.id > 0 && checkedIds.has(m.id) && !['to delete','to create','to archive','archived','current','missed','being missed','not found'].includes(m.tag))
          updated = deleteProgram(updated, i);
      }
      if (updated === prev) return prev;
      onBroadcastedMediasChange(updated);
      return updated;
    });
    setCheckedIds(new Set());
    setActionMenuOpen(false);
  };

  // Sélection façon liste : clic = cette ligne seule, Ctrl/⌘+clic = ajoute ou retire une
  // ligne (sélection disjointe), Maj+clic = plage depuis la dernière ligne cliquée, dans
  // l'ordre affiché. Seules les lignes supprimables sont sélectionnables.
  const isSelectable = (m: TaggedEnrichedBroadcastedMedia) =>
    m.id > 0 && !['to delete','to create','to archive','archived','current','missed','being missed','not found'].includes(m.tag);

  const handleRowClick = (e: React.MouseEvent, m: TaggedEnrichedBroadcastedMedia) => {
    // Boutons, listes déroulantes, cases : leur propre clic, pas une sélection de ligne.
    if ((e.target as HTMLElement).closest('button, select, input, a, [data-drag-handle]')) return;
    if (!isSelectable(m)) return;
    if (e.shiftKey && checkAnchorRef.current !== null) {
      const visible = filteredMediasRef.current;
      const a = visible.findIndex(x => x.id === checkAnchorRef.current);
      const b = visible.findIndex(x => x.id === m.id);
      if (a !== -1 && b !== -1) {
        const [from, to] = a < b ? [a, b] : [b, a];
        const range = visible.slice(from, to + 1).filter(isSelectable).map(x => x.id);
        setCheckedIds(prev => new Set([...(e.ctrlKey || e.metaKey ? Array.from(prev) : []), ...range]));
        return;
      }
    }
    checkAnchorRef.current = m.id;
    if (e.ctrlKey || e.metaKey) {
      setCheckedIds(prev => {
        const next = new Set(prev);
        if (!next.delete(m.id)) next.add(m.id);
        return next;
      });
    } else {
      setCheckedIds(prev => (prev.size === 1 && prev.has(m.id)) ? new Set() : new Set([m.id]));
    }
  };

  const handleUndoDelete = (index: number) => {
    setMedias(prev => {
      const updated = restoreProgram(prev, index);
      if (updated === prev) return prev;
      onBroadcastedMediasChange(updated);
      return updated;
    });
  };

  const handleUndoCreate = (index: number) => {
    setMedias(prev => {
      const updated = cancelNewProgram(prev, index);
      if (updated === prev) return prev;
      onBroadcastedMediasChange(updated);
      return updated;
    });
  };

  const handleMediaSelection = (id: number) => {
    setMediaSelection(false);
    const found = mediaSet.find(m => m.id === id);
    if (!found) return;
    setChoosenMedia(found);
    setAudioTracks([]);
    setAudioTrackIndex(null);
    lastSelectedMediaIdRef.current = found.id;
    let dur = 0;
    if      (found.mediaType === 'Video')          dur = (found as Video).computedDuration;
    else if (found.mediaType === 'AudioSlideshow') dur = convertTimeToSeconds((found as AudioSlideshow).imageDisplayDuration);
    setChoosenDuration(dur);

    if (found.mediaType === 'Video') {
      getVideoAudioTracks(found.id).then((response) => {
        if (response.code === 1 && response.payload.length > 1) {
          setAudioTracks(response.payload);
        }
      });

      // Refus DÈS le choix : une vidéo dont le fichier est illisible (piste audio à 0 canal)
      // n'est pas proposée au programme, avant même « Ajouter ». Le serveur la refuserait de
      // toute façon à l'enregistrement — inutile de laisser l'opérateur la composer d'abord.
      // Ignoré si l'opérateur a choisi une autre vidéo entre-temps.
      getVideoPlayability(found.id).then((playability) => {
        if (playability.playable || lastSelectedMediaIdRef.current !== found.id) return;

        setChoosenMedia(initializedMedia());
        setChoosenDuration(0);
        setAudioTracks([]);
        setAudioTrackIndex(null);
        setAddMode('none');
        setShowConfirmMsg(`« ${found.name} » ne peut pas être ajoutée au programme : ${playability.reason}.`);
      });
    }
  };

  const handleAudioTrackChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setAudioTrackIndex(value === '' ? null : parseInt(value));
  };

  const searchNextDate = (): Date => {
    if (addMode === 'queue' || !dateAndTimeAfterLastBroadcastedMedia) {
      const last = medias.filter(m => m.tag !== 'to delete').slice(-1)[0];
      if (!last?.broadcastDate) return new Date();
      // Fin RÉELLE du dernier (fin du tour en cours d'une boucle tout juste désarmée), et
      // jamais dans le passé : « à la suite » d'un programme déjà fini, c'est maintenant.
      // Avant, une boucle désarmée renvoyait la fin de son premier passage — vidéo ajoutée
      // posée 5 min dans le passé, constaté le 21/10/2026 sur Alès TV.
      const end = effectiveEndMs(last);
      return new Date(Number.isFinite(end) ? Math.max(end, Date.now()) : Date.now());
    }
    return convertStringToDate(dateAndTime, 'dd/MM/yyyy HH:mm:ss') ?? new Date();
  };

  // ── Programmation automatique ─────────────────────────────────────────────
  // Réglages mémorisés par navigateur (confort de l'opérateur, pas une donnée de la chaîne).
  const AUTO_SETTINGS_KEY = 'kleon.autoProgram.settings';
  type AutoMethod = 'random';
  const [autoSettingsOpen, setAutoSettingsOpen] = useState(false);
  const [autoHours,        setAutoHours]        = useState<string>(() => {
    try { return JSON.parse(localStorage.getItem(AUTO_SETTINGS_KEY) || '{}').hours ?? '6'; } catch { return '6'; }
  });
  const [autoMethod,       setAutoMethod]       = useState<AutoMethod>('random');
  const [autoStartMode,    setAutoStartMode]    = useState<'after' | 'date'>(() => {
    try { return JSON.parse(localStorage.getItem(AUTO_SETTINGS_KEY) || '{}').startMode === 'date' ? 'date' : 'after'; } catch { return 'after'; }
  });
  const [autoStartDate,    setAutoStartDate]    = useState(getDefaultDateTime());

  const autoDurationSeconds = (): number => {
    const h = parseFloat(autoHours.replace(',', '.'));
    return Number.isFinite(h) && h > 0 ? Math.round(h * 3600) : 0;
  };

  const saveAutoSettings = () => {
    try { localStorage.setItem(AUTO_SETTINGS_KEY, JSON.stringify({ hours: autoHours, startMode: autoStartMode })); } catch { /* sans mémoire locale : réglages par défaut au prochain affichage */ }
  };

  // Tire au hasard parmi les vidéos du catalogue ayant « Programmation automatique » activé, sans répéter tant qu'il en reste d'inédites
  // dans le lot ni enchaîner deux fois la même, et sans dépasser la durée demandée : un
  // programme ne se coupe pas, on ne retient que ce qui tient dans le temps restant.
  const pickRandomBatch = (targetSeconds: number, catalog: Media[]): Video[] => {
    const pool = catalog.filter((m): m is Video => m.mediaType === 'Video' && (m as Video).automaticPlaylist && (m as Video).computedDuration > 0);
    const picked: Video[] = [];
    const used = new Set<number>();
    let total = 0;
    while (picked.length < 2000) {
      const fits = pool.filter(v => v.computedDuration <= targetSeconds - total);
      if (fits.length === 0) break;
      let candidates = fits.filter(v => !used.has(v.id));
      if (candidates.length === 0) { used.clear(); candidates = fits; }
      const last = picked[picked.length - 1];
      if (candidates.length > 1 && last) candidates = candidates.filter(v => v.id !== last.id);
      const v = candidates[Math.floor(Math.random() * candidates.length)];
      picked.push(v); used.add(v.id); total += v.computedDuration;
    }
    return picked;
  };

  const handleAutoProgram = async () => {
    if (!onNewBroadcastedMedia) return;
    const target = autoDurationSeconds();
    if (target === 0) { setShowConfirmMsg("La durée du batch n'est pas valide : ouvrez les réglages (engrenage) pour la corriger."); return; }

    // Même règle que « Ajouter à la suite » (fin réelle du dernier, jamais dans le passé),
    // ou l'heure saisie dans les réglages.
    let cursorMs: number;
    if (autoStartMode === 'date') {
      const d = convertStringToDate(autoStartDate, 'dd/MM/yyyy HH:mm:ss');
      if (!d) { setShowConfirmMsg("L'heure de début du batch n'est pas valide."); return; }
      cursorMs = d.getTime();
    } else {
      const last = medias.filter(m => m.tag !== 'to delete').slice(-1)[0];
      const end  = last?.broadcastDate ? effectiveEndMs(last) : NaN;
      cursorMs   = Number.isFinite(end) ? Math.max(end, Date.now()) : Date.now();
    }

    // Catalogue relu au clic : le réglage « Programmation automatique » d'une fiche vidéo a pu
    // changer depuis l'ouverture de la chaîne. Repli sur la photo existante si la relecture échoue.
    const catalog = (onRefreshMedias ? await onRefreshMedias().catch(() => null) : null) ?? mediaSet;
    const batch = pickRandomBatch(target, catalog);
    if (batch.length === 0) {
      setShowConfirmMsg("Aucune vidéo n'est disponible pour la programmation automatique (réglage « Programmation automatique » de la fiche vidéo) ou aucune ne tient dans la durée demandée.");
      return;
    }
    // Chaque vidéo est ajoutée comme si l'opérateur l'avait ajoutée seule : elle se déplace
    // et se supprime individuellement.
    for (const v of batch) {
      onNewBroadcastedMedia({
        id: 0, mediaId: v.id, broadcastDate: new Date(cursorMs),
        media: v, status: 'not_broadcasted', actualStartTime: null,
        duration: v.computedDuration, title: v.name, thumbnail: v.thumbnail,
        tag: 'to create', oldTag: '', selected: false, audioTrackIndex: null,
        loop: false, priority: false,
      });
      cursorMs += v.computedDuration * 1000;
    }
    saveAutoSettings();
    setShowConfirmMsg(`${batch.length} vidéo${batch.length > 1 ? 's' : ''} ajoutée${batch.length > 1 ? 's' : ''} au programme (${formatDurationInSeconds(batch.reduce((a, v) => a + v.computedDuration, 0))}), jusqu'à ${format(new Date(cursorMs), "dd/MM HH:mm", { locale: fr })}.`);
  };

  const handleAddConfirm = () => {
    if (!choosenMedia.id || !onNewBroadcastedMedia) return;
    const newMedia: TaggedEnrichedBroadcastedMedia = {
      // « Lire maintenant » : heure provisoire, remise à l'heure de l'envoi par
      // BroadcastingChannelSheet, puis remplacée par celle d'admin-api.
      id: 0, mediaId: choosenMedia.id, broadcastDate: addMode === 'now' ? new Date() : searchNextDate(),
      media: choosenMedia, status: 'not_broadcasted', actualStartTime: null,
      duration: choosenDuration, title: choosenMedia.name, thumbnail: choosenMedia.thumbnail,
      tag: 'to create', oldTag: '', selected: false, audioTrackIndex: audioTrackIndex,
      loop: false, priority: addMode === 'now',
    };
    onNewBroadcastedMedia(newMedia);
    setChoosenMedia(initializedMedia());
    setChoosenDuration(0);
    setAudioTracks([]);
    setAudioTrackIndex(null);
    setAddMode('none');
    setDateAndTime(getDefaultDateTime());
  };

  // « Lire maintenant » : part de chaque programme recouverte par un prioritaire (null sinon).
  // Un prioritaire qui recouvre quelque chose n'a pas de tuile à lui : ses infos sont dans le
  // bloc posé par-dessus ce qu'il recouvre (cf. priorityBlocks), et sa tuile fausserait
  // l'échelle de temps de ce bloc.
  // Un prioritaire pas encore envoyé partira à l'enregistrement, pas au clic sur « Confirmer » :
  // son aperçu suit l'heure courante. Une fois enregistré, reload() rapporte la date retenue par
  // admin-api (la sienne, cf. BroadcastedMediaCreation3), sur laquelle le bloc se redessine.
  const timeline   = medias.map(m => m.priority && m.tag === 'to create' ? { ...m, broadcastDate: progressClock } : m);
  const covers     = timeline.map((_, i) => priorityCover(timeline, i));
  // Indices (dans medias) des prioritaires qui recouvrent au moins un programme.
  const overlaidPriorities = new Set(covers.filter((c): c is PriorityCover => !!c).map(c => timeline.indexOf(c.by)));
  // À l'antenne d'après les heures, sans attendre le statut 'current' d'admin-api.
  const liveIdx    = channelActive ? liveProgramIndex(timeline, progressClock.getTime()) : -1;

  const filteredMedias = (() => {
    const now = new Date();
    return medias.filter((_, i) => !overlaidPriorities.has(i)).filter(m => {
      const d   = m.broadcastDate ? new Date(m.broadcastDate) : null;
      if (activeTab === 'today') {
        if (!d) return true;
        const tomorrow = new Date(); tomorrow.setHours(0,0,0,0); tomorrow.setDate(tomorrow.getDate() + 1);
        return d < tomorrow;
      }
      if (activeTab === 'upcoming') return !!d && d > now;
      if (activeTab === 'broadcasted')
        return ['to archive', 'archived'].includes(m.tag);
      return true;
    });
  })();

  const grouped: { dayKey: string; label: string; items: { media: TaggedEnrichedBroadcastedMedia; globalIndex: number }[] }[] = [];
  let currentDayKey = '';
  filteredMedias.forEach(m => {
    const globalIndex = medias.indexOf(m);
    const d  = m.broadcastDate ? new Date(m.broadcastDate) : null;
    const dk = d ? `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}` : 'nodate';
    if (dk !== currentDayKey) { currentDayKey = dk; grouped.push({ dayKey: dk, label: formatDayLabel(d), items: [] }); }
    grouped[grouped.length - 1].items.push({ media: m, globalIndex });
  });

  const tagClass = (tag: string) => {
    const map: Record<string, string> = {
      'to create': styles.tagCreate, 'to update': styles.tagUpdate,
      'to delete': styles.tagDelete, 'to archive': styles.tagArchive,
      'queued': styles.tagQueued,    'current': styles.tagCurrent,
    };
    const extra = ['missed','being missed','not found'].includes(tag) ? styles.tagError : (map[tag] ?? '');
    return classNames(styles.tagBadge, extra);
  };

  const tagLabel = (tag: string) => ({
    'to create': 'Nouveau', 'to update': 'Modifié', 'to delete': 'Supprimé',
    'to archive': 'À archiver', 'queued': 'En attente', 'current': 'EN COURS',
    'missed': 'Non diffusé', 'being missed': 'Non diffusé', 'not found': 'Introuvable',
  } as Record<string, string>)[tag] ?? '';

  const scheduledCount = medias.filter(m => !['to archive','archived','missed','being missed','not found'].includes(m.tag)).length;
  const totalSec       = totalDuration(medias);
  const nextMedia      = medias.find(m => m.tag === 'queued' || (m.tag === '' && m.broadcastDate && new Date(m.broadcastDate) > new Date()));
  const nextTime       = nextMedia?.broadcastDate ? formatTime(new Date(nextMedia.broadcastDate)) : '—';
  const checkedCount = medias.filter(m => m.id > 0 && checkedIds.has(m.id) && !['to delete','to create','to archive','archived','current','missed','being missed','not found'].includes(m.tag)).length;
  const pendingChanges = medias.filter(m => ['to create','to update','to delete'].includes(m.tag) || m.loopDirty).length;
  const permutableList = medias.filter((_, i) => canReorder(medias, i));
  // Programme en boucle qui retient la suite : plus aucun ajout possible, faute de pouvoir
  // donner une heure de départ à ce qui viendrait après.
  const loopHolder     = loopHolderIndex(medias);
  const isDragging     = dragSrcIdx !== -1;
  const srcVisualIndex = isDragging ? filteredMedias.findIndex(m => medias.indexOf(m) === dragSrcIdx) : -1;

  // insertAfter est un indice GLOBAL (cf. computeInsertAfter / handlePointerUp) ; l'animation
  // de décalage des lignes raisonne, elle, en indices VISUELS (position dans filteredMedias).
  // Sans cette conversion, les deux espaces ne coïncident que si le filtre de l'onglet est un
  // préfixe de la liste.
  const insertAfterVisual = insertAfter < 0
    ? insertAfter
    : filteredMedias.filter(m => medias.indexOf(m) <= insertAfter).length - 1;

  insertAfterRef.current    = insertAfter;
  srcVisualIndexRef.current = srcVisualIndex;
  filteredMediasRef.current = filteredMedias;
  mediasRef.current         = medias;

  const renderRow = (m: TaggedEnrichedBroadcastedMedia, globalIndex: number, visualIndex: number) => {
    // D'après les heures (liveProgramIndex) : la vidéo coupée par un « Lire maintenant » perd
    // son « EN COURS », celle dont l'heure vient d'arriver l'affiche sans attendre le webhook.
    const isLive      = globalIndex === liveIdx;
    const isDeleted   = m.tag === 'to delete';
    const isArchived  = ['to archive','archived'].includes(m.tag);
    const isHeld      = isHeldByLoop(medias, globalIndex);
    const isLoop      = m.loop && !isDeleted && !isArchived;
    const isDraggable = canReorder(medias, globalIndex);
    const pi          = permutableList.indexOf(m);
    const canMoveUp   = isDraggable && pi > 0;
    const canMoveDown = isDraggable && pi < permutableList.length - 1;
    const canDelete   = !['to delete','to create','to archive','archived','current','missed','being missed','not found'].includes(m.tag);
    // Boucle réglable partout sauf sur ce qui est retenu (grisé) ou ne jouera plus.
    const canToggleLoop = !isHeld && !isDeleted && !isArchived && !m.priority && !['missed','being missed','not found'].includes(m.tag);
    const isBandeau   = bandeauStates[globalIndex] ?? false;
    const isPub       = pubStates[globalIndex] ?? true;
    // Fini d'après les heures : « À archiver » tout de suite, sans attendre le webhook. Une
    // archive qui n'a pas été jouée (sautée, en échec) reste « Non diffusé ».
    const notPlayed   = ['missed', 'being missed', 'not found'].includes(m.tag)
                        || (m.tag === 'archived' && ['skipped', 'missed', 'failed', 'not_found'].includes(m.status));
    const ended       = !isLive && !isHeld && hasEnded(m, progressClock.getTime());
    const tag         = isHeld ? 'Suspendu'
                      : notPlayed && ended ? 'Non diffusé'
                      : ended ? 'À archiver'
                      : tagLabel(m.tag);
    const isSource    = dragSrcIdx === globalIndex;
    const rowAudioTracks   = audioTracksCache[m.mediaId] ?? [];
    // Même bornage que reorderProgram/handleDelete : au-delà ('to delete' compris), le
    // programme est figé, la langue ne peut plus être qu'affichée, jamais rechoisie.
    const canEditAudioTrack = isDraggable && !isDeleted;

    // Aucune action disponible → on n'affiche pas le bouton "..."
    const hasMenuItems = canMoveUp || canMoveDown || isDeleted || m.tag === 'to create' || canDelete;

    // Progression du programme EN COURS uniquement — même calcul que le
    // tableau de bord (BroadcastingChannelDashboard) : départ réel relevé auprès du
    // microservice (liveStart), broadcastDate à défaut ; bornée à [0, duration].
    let elapsedSeconds: number | null = null;
    let progressRatio = 0;
    if (isLive && m.duration > 0 && m.broadcastDate) {
      const startMs = liveStart?.bmId === m.id ? liveStart.startMs : new Date(m.broadcastDate).getTime();
      if (!Number.isNaN(startMs)) {
        const sinceStart = Math.max(0, (progressClock.getTime() - startMs) / 1000);
        // En boucle : progression dans le tour en cours (le modulo couvre une relance
        // survenue depuis le dernier relevé).
        elapsedSeconds = isLoop ? sinceStart % m.duration : Math.min(m.duration, sinceStart);
        progressRatio  = elapsedSeconds / m.duration;
      }
    }

    let translateY = 0;
    if (isDragging && dragRef.current && srcVisualIndex !== -1) {
      const h    = dragRef.current.rowHeight;
      const diff = insertAfterVisual - srcVisualIndex;
      if (isSource) {
        if (diff > 0)      translateY = diff * h;
        else if (diff < 0) translateY = (diff + 1) * h;
      } else {
        if (srcVisualIndex < insertAfterVisual) {
          if (visualIndex > srcVisualIndex && visualIndex <= insertAfterVisual) translateY = -h;
        } else if (srcVisualIndex > insertAfterVisual) {
          if (visualIndex > insertAfterVisual && visualIndex <= srcVisualIndex - 1) translateY = h;
        }
      }
    }

    return (
      <React.Fragment key={`${globalIndex}-${m.id}`}>
        <div
          ref       = {el => { rowRefs.current[globalIndex] = el; }}
          className = {classNames(
            styles.programRow,
            isLive    && styles.programRowLive,
            isDeleted && styles.programRowDeleted,
            isArchived && styles.programRowArchived,
            isHeld    && styles.programRowHeld,
            isSource  && styles.programRowSource,
            canDelete && m.id > 0 && checkedIds.has(m.id) && styles.programRowChecked,
          )}
          onMouseDown   = {(e) => { if (e.shiftKey) e.preventDefault(); }}
          onClick       = {(e) => handleRowClick(e, m)}
          style={{
            transform    : `translateY(${translateY}px)`,
            transition   : isDragging ? 'transform 0.2s cubic-bezier(0.25,0.46,0.45,0.94)' : 'transform 0.22s cubic-bezier(0.25,0.46,0.45,0.94)',
            opacity      : isSource ? 0.12 : 1,
            position     : 'relative',
            zIndex       : isSource ? 0 : undefined,
            pointerEvents: isDragging && isSource ? 'none' : undefined,
            // Reprendra en cours après un « Lire maintenant » : sa part recouverte devient un
            // espace en tête de tuile, sous le bloc ; titre et jauge restent entiers dessous.
            paddingTop   : resumeSpacers[globalIndex] !== undefined ? ROW_PADDING_PX + resumeSpacers[globalIndex] : undefined,
          }}
          onPointerDown = {isDraggable ? (e) => handlePointerDown(e, globalIndex) : undefined}
          onPointerMove = {isDragging  ? handlePointerMove : undefined}
          onPointerUp   = {isDragging  ? handlePointerUp   : undefined}
        >
          <div className={classNames(styles.dragHandle, !isDraggable && styles.dragHandleLocked)} data-drag-handle="true" title={isDraggable ? 'Glisser pour déplacer' : isHeld ? 'Suspendu tant que la boucle tourne' : isLoop ? 'Joue en boucle' : 'Déjà diffusé ou en cours'}>
            {isDraggable ? <Icons.Grip/> : <Icons.Lock/>}
          </div>
          <div className={styles.thumbnailColumn}>
            <div className={styles.thumbnailWrapper}>
              <img className={styles.thumbnail} src={`${WWW_URLS.MediaServer}${m.media?.publicRef}/images/thumbnail.webp`} alt={m.media?.name ?? ''} onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }}/>
              <span className={styles.durationBadge}>{isLoop ? '∞' : formatDurationInSeconds(m.duration)}</span>
            </div>
            {elapsedSeconds !== null && (
              <div className={styles.progressTrackWrap}>
                <div className={styles.progressTrack}>
                  <div className={styles.progressFill} style={{ width: `${progressRatio * 100}%` }}/>
                </div>
              </div>
            )}
          </div>
          {m.broadcastDate && <div className={styles.programTime}>{formatTime(new Date(m.broadcastDate))}</div>}
          <div className={styles.programBody}>
            <div className={styles.programTitle}>{m.media?.name ?? m.title}</div>
            <div className={styles.programMeta}>
              {isLive && <span className={styles.liveBadge}><span className={styles.liveDot}/>EN COURS</span>}
              {m.priority && <span className={styles.priorityBadge}><Icons.Play/>Lire maintenant</span>}
              <span className={styles.metaIcon}><Icons.Clock/></span>
              <span>{formatDurationInSeconds(m.duration)}</span>
              {isLoop && <span className={styles.loopBadge}><Icons.Repeat/>En boucle</span>}
              {elapsedSeconds !== null && (
                <span className={styles.watchedTime}>
                  <span className={styles.metaIcon}><Icons.Eye/></span>{formatDurationInSeconds(elapsedSeconds)}
                </span>
              )}
              {/* isLive affiche déjà liveBadge ("EN COURS") : tagLabel('current')
                  renvoie le même texte, un second badge le répéterait pour rien. */}
              {/* Ni le statut 'current' d'admin-api : il reste sur la vidéo qu'un « Lire
                  maintenant » a coupée ; seul liveProgramIndex dit ce qui est à l'antenne. */}
              {tag && !isLive && (m.tag !== 'current' || ended) && <span className={isHeld ? classNames(styles.tagBadge, styles.tagHeld) : tagClass(ended && !notPlayed ? 'to archive' : notPlayed ? 'missed' : m.tag)}>{tag}</span>}
            </div>
          </div>
          <div className={styles.programControls}>
            {rowAudioTracks.length > 1 && (
              canEditAudioTrack ? (
                <div className={styles.audioTrackCell}>
                  <Select choices         = {rowAudioTracks.map((t) => `${t.index}`)}
                          choicesLabel    = {rowAudioTracks.map((t) => t.label)}
                          value           = {m.audioTrackIndex !== null ? `${m.audioTrackIndex}` : ''}
                          onChange        = {(e) => handleAudioTrackEdit(globalIndex, e.target.value === '' ? null : parseInt(e.target.value))}
                          placeholder     = "Langue"
                          showPlaceholder = {true}
                          width           = "110px"
                          height          = "24px"           />
                </div>
              ) : (
                <span className={styles.audioTrackLabel}>
                  {(rowAudioTracks[m.audioTrackIndex ?? 0] ?? rowAudioTracks[0]).label}
                </span>
              )
            )}
            <div className={classNames(styles.pubToggle, !canToggleLoop && styles.toggleDisabled)}
                 title={isHeld ? 'Suspendu derrière un programme en boucle' : 'Rejoue la vidéo indéfiniment ; les programmes suivants sont suspendus'}>
              <span>Jouer en boucle</span>
              <Toggle checked={m.loop} readOnly={!canToggleLoop} onChange={(v) => handleLoopToggle(globalIndex, v)} form="switch"/>
            </div>
            <div className={styles.pubToggle}>
              <span>Pub</span>
              <Toggle checked={isPub} onChange={(v) => setPubStates(prev => ({ ...prev, [globalIndex]: v }))} form="switch"/>
            </div>
            <button className={classNames(styles.bandeauBtn, isBandeau && styles.bandeauBtnActive)} onClick={() => setBandeauStates(prev => ({ ...prev, [globalIndex]: !isBandeau }))}>
              <Icons.Bookmark/>Billboard
            </button>
            <div style={{ position: 'relative' }}>
              {hasMenuItems ? (
                <button
                  ref={el => { if (el) menuBtnRefs.current.set(globalIndex, el); else menuBtnRefs.current.delete(globalIndex); }}
                  className={styles.menuBtn}
                  onClick={() => openMenu(globalIndex)}
                >
                  <Icons.Dots/>
                </button>
              ) : (
                <div className={styles.menuBtnPlaceholder}/>
              )}
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  };

  rowRefs.current = rowRefs.current.slice(0, medias.length);

  // ── Blocs « Lire maintenant » ─────────────────────────────────────────────────────────
  // Un bloc par prioritaire, posé PAR-DESSUS les tuiles qu'il recouvre : une bande hachurée
  // puis les infos du prioritaire ; à sa gauche, vignette, heure et début du titre des tuiles
  // recouvertes restent visibles. Sa hauteur suit le temps : il commence dans la première tuile
  // au point où elle est coupée (cover.from) et finit dans la dernière au point où la grille
  // reprend (cover.to).
  // Dernière tuile d'un bloc, qui reprendra en cours après le prioritaire : hauteur
  // de l'espace qu'on lui ajoute en tête, à l'échelle d'une tuile ordinaire. Le bloc s'arrête
  // au bas de cet espace (cf. useLayoutEffect ci-dessous).
  const resumeSpacers: Record<number, number> = {};
  const priorityBlocks = Array.from(overlaidPriorities).map(pIndex => {
    const p    = timeline[pIndex];
    const rows = filteredMedias.map(m => medias.indexOf(m)).filter(i => covers[i]?.by === p);
    if (rows.length === 0) return null;
    const last = covers[rows[rows.length - 1]];
    // Toute tuile qui reprendra après le prioritaire, y compris celle qu'il a coupée (bloc au
    // milieu d'une seule tuile) : son contenu passe sous le bloc.
    if (last && last.to < 1) {
      // À l'antenne (le prioritaire a fini) : hauteur doublée, ses infos s'affichent entières sous
      // le bloc ; avant, l'espace suit la part recouverte.
      resumeSpacers[rows[rows.length - 1]] = rows[rows.length - 1] === liveIdx
        ? ROW_HEIGHT_PX
        : Math.round(last.to * ROW_HEIGHT_PX);
    }
    return { p, pIndex, key: p.id ? `${p.id}` : `new-${pIndex}`, first: rows[0], last: rows[rows.length - 1] };
  }).filter((b): b is NonNullable<typeof b> => b !== null);

  // Mesuré sur les tuiles réellement rendues (leur hauteur varie : sélecteur de langue...),
  // avant affichage ; ne relance un rendu que si la géométrie a changé.
  const [blockBoxes, setBlockBoxes] = useState<Record<string, { top: number; height: number }>>({});
  useLayoutEffect(() => {
    const next: Record<string, { top: number; height: number }> = {};
    for (const b of priorityBlocks) {
      const first = rowRefs.current[b.first];
      const last  = rowRefs.current[b.last];
      const cf = covers[b.first], cl = covers[b.last];
      if (!first || !last || !cf || !cl) continue;
      const spacer = resumeSpacers[b.last];
      // Tuile à la fois coupée et reprise : l'espace de tête représente [0, to] de son temps,
      // le bloc y couvre [from, to].
      const top    = spacer !== undefined && b.first === b.last
        ? first.offsetTop + spacer * (cf.from / cf.to)
        : first.offsetTop + cf.from * first.offsetHeight;
      const bottom = spacer !== undefined
        ? last.offsetTop + spacer
        : last.offsetTop + cl.to * last.offsetHeight;
      next[b.key]  = { top: Math.round(top), height: Math.round(Math.max(0, bottom - top)) };
    }
    if (JSON.stringify(next) !== JSON.stringify(blockBoxes)) setBlockBoxes(next);
  });

  const renderPriorityBlock = (b: typeof priorityBlocks[number]) => {
    const box = blockBoxes[b.key];
    if (!box) return null;
    const p      = b.p;
    const isLive = b.pIndex === liveIdx;
    const ended  = !isLive && hasEnded(p, progressClock.getTime());
    let elapsed: number | null = null;
    if (isLive && p.duration > 0 && p.broadcastDate) {
      const startMs = liveStart && liveStart.bmId === p.id ? liveStart.startMs : new Date(p.broadcastDate).getTime();
      if (!Number.isNaN(startMs)) elapsed = Math.min(p.duration, Math.max(0, (progressClock.getTime() - startMs) / 1000));
    }
    const covered = filteredMedias.filter(m => covers[medias.indexOf(m)]?.by === p).length;
    return (
      <div key={b.key} className={styles.priorityBlock} style={{ top: box.top, height: box.height }}>
        <div className={styles.priorityHatch} title={`${covered} vidéo${covered > 1 ? 's' : ''} recouverte${covered > 1 ? 's' : ''}`}/>
        <div className={classNames(styles.priorityInfo, isLive && styles.priorityInfoLive)}>
          <div className={styles.thumbnailWrapper}>
            <img className={styles.thumbnail} src={`${WWW_URLS.MediaServer}${p.media?.publicRef}/images/thumbnail.webp`} alt={p.media?.name ?? ''} onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }}/>
            <span className={styles.durationBadge}>{formatDurationInSeconds(p.duration)}</span>
          </div>
          {p.broadcastDate && <div className={styles.programTime}>{formatTime(new Date(p.broadcastDate))}</div>}
          <div className={styles.priorityInfoBody}>
            <div className={styles.programTitle}>{p.media?.name ?? p.title}</div>
            <div className={styles.programMeta}>
              {isLive && <span className={styles.liveBadge}><span className={styles.liveDot}/>EN COURS</span>}
              {ended && <span className={classNames(styles.tagBadge, styles.tagArchive)}>À archiver</span>}
              {p.tag === 'to create' && <span className={classNames(styles.tagBadge, styles.tagCreate)}>Partira à « Mettre à jour le programme »</span>}
              <span className={styles.metaIcon}><Icons.Clock/></span>
              <span>{elapsed !== null ? `${formatDurationInSeconds(elapsed)} / ` : ''}{formatDurationInSeconds(p.duration)}</span>
            </div>
            {elapsed !== null && (
              <div className={styles.progressTrack}>
                <div className={styles.progressFill} style={{ width: `${(elapsed / p.duration) * 100}%` }}/>
              </div>
            )}
          </div>
          {p.tag === 'to create' && (
            <button className={styles.btnSecondary} onClick={() => handleUndoCreate(b.pIndex)}>Annuler</button>
          )}
        </div>
        <div className={styles.priorityHatch}/>
      </div>
    );
  };

  return (
    <div className={styles.root}>

      {mediaSelection && ReactDOM.createPortal(
        <MediaSelector mediaSet={mediaSet} mediaCategories={mediaCategories}
          onCancel={() => setMediaSelection(false)} onSelect={handleMediaSelection}
          originalFilter={['Video', 'AudioSlideshow']}/>,
        document.body,
      )}

      {/* ── Context menu portal — toujours au-dessus de tout ── */}
      {openMenuIndex !== null && menuPosition && (() => {
        const idx        = openMenuIndex;
        const m          = medias[idx];
        if (!m) return null;
        const isDraggable2 = canReorder(medias, idx);
        const pi2          = permutableList.indexOf(m);
        const canMoveUp2   = isDraggable2 && pi2 > 0;
        const canMoveDown2 = isDraggable2 && pi2 < permutableList.length - 1;
        const isDeleted2   = m.tag === 'to delete';
        const canDelete2   = !['to delete','to create','to archive','archived','current','missed','being missed','not found'].includes(m.tag);
        return ReactDOM.createPortal(
          <div
            ref={menuRef}
            className={styles.contextMenu}
            style={{
              position : 'fixed',
              top      : menuPosition.top,
              left     : menuPosition.left,
              transform: 'translateX(-100%)',
              zIndex   : 99999,
            }}
          >
            {canMoveUp2   && <button className={styles.contextMenuItem} onClick={() => { handleMove(idx, 'up');   setOpenMenuIndex(null); }}><Icons.Up/>Monter</button>}
            {canMoveDown2 && <button className={styles.contextMenuItem} onClick={() => { handleMove(idx, 'down'); setOpenMenuIndex(null); }}><Icons.Down/>Descendre</button>}
            {(canMoveUp2 || canMoveDown2) && <div className={styles.contextMenuDivider}/>}
            {isDeleted2         && <button className={styles.contextMenuItem} onClick={() => { handleUndoDelete(idx); setOpenMenuIndex(null); }}><Icons.Undo/>Annuler suppression</button>}
            {m.tag === 'to create' && <button className={styles.contextMenuItem} onClick={() => { handleUndoCreate(idx); setOpenMenuIndex(null); }}><Icons.Undo/>Annuler ajout</button>}
            {canDelete2  && <button className={classNames(styles.contextMenuItem, styles.contextMenuDanger)} onClick={() => handleDelete(idx)}><Icons.Trash/>Supprimer</button>}
          </div>,
          document.body,
        );
      })()}

      {showConfirmMsg && (
        <Window message={<>{showConfirmMsg}</>} messageType="information"
          onClickOnValidateButton={() => setShowConfirmMsg(null)} validButtonText="Ok"/>
      )}

      {/* ── Page header ─────────────────────────────────────────────────── */}
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Programme TV</h1>
          <p className={styles.pageSubtitle}>Organisez facilement ce qui va passer à l'antenne.</p>
        </div>

        {/* Save actions — déplacés ici depuis la save bar */}
        {onSaveSchedule && medias.length > 0 && (
          <div className={styles.headerSaveArea}>
            {pendingChanges > 0 && (
              <span className={styles.saveBarMessage}>
                {pendingChanges} modification{pendingChanges > 1 ? 's' : ''} en attente
              </span>
            )}
            <button
              className={classNames(styles.btnPrimary, pendingChanges > 0 ? styles.btnSave : styles.btnSaveInactive)}
              onClick={onSaveSchedule}
            >
              Mettre à jour le programme
            </button>
          </div>
        )}
      </div>

      {!channelActive && (
        <div className={styles.inactiveWarning}><Icons.Warning/>La chaîne est inactive — les programmes ne seront pas diffusés.</div>
      )}

      {/* ── Body : grid 2 colonnes (contenu gauche | sidebar) ───────────── */}
      <div className={styles.bodyGrid}>

        {/* ── Colonne gauche ───────────────────────────────────────────── */}
        <div className={styles.bodyLeft}>



          {/* Formulaires d'ajout inline */}
          {choosenMedia.id !== 0 && addMode === 'date' && (
            <div className={styles.addMediaForm}>
              <img className={styles.choosenMediaThumb} src={`${WWW_URLS.MediaServer}${choosenMedia.publicRef}/images/thumbnail.webp`} alt={choosenMedia.name} onClick={() => setMediaSelection(true)}/>
              <div className={styles.addMediaFormLabel}>{choosenMedia.name}</div>
              {audioTracks.length > 1 && (
                <div className={styles.audioTrackRow}>
                  <span className={styles.addMediaFormLabel}>Piste audio :</span>
                  <Select choices         = {audioTracks.map((t) => `${t.index}`)}
                          choicesLabel    = {audioTracks.map((t) => t.label)}
                          value           = {audioTrackIndex !== null ? `${audioTrackIndex}` : ''}
                          onChange        = {handleAudioTrackChange}
                          placeholder     = "Choisir la langue"
                          showPlaceholder = {true}                    />
                </div>
              )}
              <div className={styles.dateTimeRow}><DateTimeSelector value={dateAndTime} onChange={setDateAndTime} width="fit-content" showSeconds={true}/></div>
              <div className={styles.addMediaFormActions}>
                <button className={styles.btnPrimary}   onClick={handleAddConfirm}>Ajouter</button>
                <button className={styles.btnSecondary} onClick={() => { setAddMode('none'); setChoosenMedia(initializedMedia()); setAudioTracks([]); setAudioTrackIndex(null); }}>Annuler</button>
              </div>
            </div>
          )}
          {choosenMedia.id !== 0 && addMode === 'now' && (
            <div className={styles.addMediaForm}>
              <img className={styles.choosenMediaThumb} src={`${WWW_URLS.MediaServer}${choosenMedia.publicRef}/images/thumbnail.webp`} alt={choosenMedia.name} onClick={() => setMediaSelection(true)}/>
              <div className={styles.addMediaFormLabel}>
                {choosenMedia.name} — coupe le programme en cours et le recouvre pendant {formatDurationInSeconds(choosenDuration)} ;
                la grille reprend ensuite là où elle en serait
              </div>
              {audioTracks.length > 1 && (
                <div className={styles.audioTrackRow}>
                  <span className={styles.addMediaFormLabel}>Piste audio :</span>
                  <Select choices         = {audioTracks.map((t) => `${t.index}`)}
                          choicesLabel    = {audioTracks.map((t) => t.label)}
                          value           = {audioTrackIndex !== null ? `${audioTrackIndex}` : ''}
                          onChange        = {handleAudioTrackChange}
                          placeholder     = "Choisir la langue"
                          showPlaceholder = {true}                    />
                </div>
              )}
              <div className={styles.addMediaFormActions}>
                <button className={styles.btnPrimary}   onClick={handleAddConfirm}>Confirmer</button>
                <button className={styles.btnSecondary} onClick={() => { setAddMode('none'); setChoosenMedia(initializedMedia()); setAudioTracks([]); setAudioTrackIndex(null); }}>Annuler</button>
              </div>
            </div>
          )}
          {choosenMedia.id !== 0 && addMode === 'queue' && (
            <div className={styles.addMediaForm}>
              <img className={styles.choosenMediaThumb} src={`${WWW_URLS.MediaServer}${choosenMedia.publicRef}/images/thumbnail.webp`} alt={choosenMedia.name} onClick={() => setMediaSelection(true)}/>
              <div className={styles.addMediaFormLabel}>{choosenMedia.name} — sera diffusé à la suite</div>
              {audioTracks.length > 1 && (
                <div className={styles.audioTrackRow}>
                  <span className={styles.addMediaFormLabel}>Piste audio :</span>
                  <Select choices         = {audioTracks.map((t) => `${t.index}`)}
                          choicesLabel    = {audioTracks.map((t) => t.label)}
                          value           = {audioTrackIndex !== null ? `${audioTrackIndex}` : ''}
                          onChange        = {handleAudioTrackChange}
                          placeholder     = "Choisir la langue"
                          showPlaceholder = {true}                    />
                </div>
              )}
              <div className={styles.addMediaFormActions}>
                <button className={styles.btnPrimary}   onClick={handleAddConfirm}>Confirmer</button>
                <button className={styles.btnSecondary} onClick={() => { setAddMode('none'); setChoosenMedia(initializedMedia()); setAudioTracks([]); setAudioTrackIndex(null); }}>Annuler</button>
              </div>
            </div>
          )}

          {/* Ligne 1 : Action cards — regroupées dans un bloc de niveau 1 */}
          {onNewBroadcastedMedia && choosenMedia.id === 0 && (
            <div className={styles.actionCardsPanel}>
              <p className={styles.actionCardsPanelTitle}>Ajouter du contenu</p>
              {loopHolder !== -1 && (
                <p className={styles.loopAddBlocked}>
                  <Icons.Repeat/>« {medias[loopHolder].media?.name ?? medias[loopHolder].title} » joue en boucle : impossible de calculer
                  l'heure de départ d'une vidéo ajoutée. Désactivez la boucle pour en ajouter.
                </p>
              )}
              <div className={classNames(styles.actionCards, loopHolder !== -1 && styles.actionCardsDisabled)}
                   aria-disabled={loopHolder !== -1}>
                <button className={classNames(styles.actionCard, styles.actionCardPlayNow)} disabled={loopHolder !== -1} onClick={() => { setAddMode('now'); setMediaSelection(true); }}>
                  <div className={styles.actionCardIcon}><Icon name = "playNow"   width = {36} height = {36} /></div>
                  <div className={styles.actionCardBody}><p className={styles.actionCardTitle}>Lire maintenant</p><p className={styles.actionCardDesc}>Diffusez tout de suite une vidéo en direct.</p></div>
                  <span className={styles.actionCardArrow}><Icons.ChevronRight/></span>
                </button>
                <button className={classNames(styles.actionCard, styles.actionCardQueue)} disabled={loopHolder !== -1} onClick={() => { setAddMode('queue'); setMediaSelection(true); }}>
                  <div className={styles.actionCardIcon}><Icon name="listAdd"   width = {36} height = {36} /></div>
                  <div className={styles.actionCardBody}><p className={styles.actionCardTitle}>Ajouter à la suite</p><p className={styles.actionCardDesc}>Ajoutez une vidéo qui sera diffusée juste après.</p></div>
                  <span className={styles.actionCardArrow}><Icons.ChevronRight/></span>
                </button>
                <button className={classNames(styles.actionCard, styles.actionCardSchedule)} disabled={loopHolder !== -1} onClick={() => { setAddMode('date'); setMediaSelection(true); }}>
                  <div className={styles.actionCardIcon}><Icon name="calendarAdd"   width = {36} height = {36} /></div>
                  <div className={styles.actionCardBody}><p className={styles.actionCardTitle}>Programmer à une date précise</p><p className={styles.actionCardDesc}>Planifiez une diffusion à l'heure de votre choix.</p></div>
                  <span className={styles.actionCardArrow}><Icons.ChevronRight/></span>
                </button>
                <div className={classNames(styles.actionCard, styles.actionCardAuto, loopHolder !== -1 && styles.actionCardAutoDisabled)}>
                  <button className={styles.actionCardAutoMain} disabled={loopHolder !== -1} onClick={handleAutoProgram}>
                    <div className={styles.actionCardIcon}><Icons.Shuffle/></div>
                    <div className={styles.actionCardBody}><p className={styles.actionCardTitle}>Programmation automatique</p><p className={styles.actionCardDesc}>{autoHours} h au hasard{autoStartMode === 'date' ? ` dès ${autoStartDate}` : ', à la suite'}.</p></div>
                  </button>
                  <button className={styles.actionCardGear} title="Réglages de la programmation automatique" aria-label="Réglages de la programmation automatique" onClick={() => setAutoSettingsOpen(true)}>
                    <Icon name="gear" width={20} height={20} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {autoSettingsOpen && ReactDOM.createPortal(
            <div className={styles.autoOverlay} onMouseDown={(e) => { if (e.target === e.currentTarget) setAutoSettingsOpen(false); }}>
              <div className={styles.autoDialog} role="dialog" aria-label="Programmation automatique">
                <h2 className={styles.autoDialogTitle}>Programmation automatique</h2>
                <label className={styles.autoField}>
                  <span className={styles.autoFieldLabel}>Durée du batch (heures)</span>
                  <input className={styles.autoInput} type="number" min="0.5" step="0.5" value={autoHours} onChange={(e) => setAutoHours(e.target.value)}/>
                </label>
                <label className={styles.autoField}>
                  <span className={styles.autoFieldLabel}>Méthode</span>
                  <select className={styles.autoInput} value={autoMethod} onChange={(e) => setAutoMethod(e.target.value as AutoMethod)}>
                    <option value="random">Au hasard</option>
                  </select>
                </label>
                <div className={styles.autoField}>
                  <span className={styles.autoFieldLabel}>Début du batch</span>
                  <label className={styles.autoRadio}><input type="radio" checked={autoStartMode === 'after'} onChange={() => setAutoStartMode('after')}/>À la suite du dernier programme</label>
                  <label className={styles.autoRadio}><input type="radio" checked={autoStartMode === 'date'}  onChange={() => setAutoStartMode('date')}/>À l'heure saisie :</label>
                  {autoStartMode === 'date' && (
                    <div className={styles.dateTimeRow}><DateTimeSelector value={autoStartDate} onChange={setAutoStartDate} width="fit-content" showSeconds={true}/></div>
                  )}
                </div>
                <p className={styles.autoHint}>Les vidéos sont tirées dans le catalogue sans dépasser la durée : la dernière peut laisser un peu de temps libre. Chacune se déplace ou se supprime ensuite individuellement.</p>
                <div className={styles.addMediaFormActions}>
                  <button className={styles.btnPrimary} onClick={() => { saveAutoSettings(); setAutoSettingsOpen(false); }}>Enregistrer</button>
                  <button className={styles.btnSecondary} onClick={() => setAutoSettingsOpen(false)}>Fermer</button>
                </div>
              </div>
            </div>,
            document.body,
          )}

          {/* Ligne 2 : Panel niveau 2 englobant la playlist — les boutons restent en dehors */}
          <div className={styles.playlistPanel}>
              {/* Tabs */}
              <div className={styles.tabBar}>
                {([
                  { key: 'today',       label: "Aujourd'hui" },
                  { key: 'upcoming',    label: 'À venir'     },
                  { key: 'broadcasted', label: 'Diffusé'     },
                ] as { key: TabFilter; label: string }[]).map(t => (
                  <button key={t.key} className={classNames(styles.tab, activeTab === t.key && styles.tabActive)} onClick={() => setActiveTab(t.key)}>
                    {t.label}
                  </button>
                ))}
                <div ref={actionMenuRef} className={styles.tabActionWrap}>
                  <button className={classNames(styles.tab, actionMenuOpen && styles.tabActive)} onClick={() => setActionMenuOpen(o => !o)}>
                    Action
                  </button>
                  {actionMenuOpen && (
                    <div className={classNames(styles.contextMenu, styles.tabActionMenu)}>
                      <button className={classNames(styles.contextMenuItem, styles.contextMenuDanger)} onClick={handleClearAll}><Icons.Trash/>Tout effacer</button>
                      <button className={styles.contextMenuItem} disabled={checkedCount === 0} onClick={handleClearChecked}><Icons.Trash/>Effacer la sélection{checkedCount > 0 ? ` (${checkedCount})` : ''}</button>
                    </div>
                  )}
                </div>
              </div>

            <div className={styles.mainPanel}>

              {/* Programme list */}
              <div
                ref       = {listRef}
                className = {classNames(styles.programList, isDragging && styles.programListDragging)}
                onPointerMove = {isDragging ? handlePointerMove : undefined}
                onPointerUp   = {isDragging ? handlePointerUp   : undefined}
              >
                {filteredMedias.length === 0 ? (
                  <div className={styles.emptyState}>
                    <div className={styles.emptyStateIcon}><Icons.Film/></div>
                    <p className={styles.emptyStateTitle}>Aucun programme à afficher</p>
                    <p className={styles.emptyStateDesc}>
                      {activeTab === 'today' ? "Ajoutez des vidéos pour remplir votre programme du jour."
                        : activeTab === 'upcoming' ? "Aucune vidéo programmée à venir."
                        : "Aucune vidéo diffusée dans cette période."}
                    </p>
                  </div>
                ) : (
                  grouped.map(group => (
                    <React.Fragment key={group.dayKey}>
                      {group.label && <div className={styles.dayHeader}>{group.label}<span className={styles.dayHeaderLine}/></div>}
                      {group.items.map(({ media, globalIndex }) => {
                        const absVisualIndex = filteredMedias.indexOf(media);
                        return renderRow(media, globalIndex, absVisualIndex);
                      })}
                    </React.Fragment>
                  ))
                )}
                {priorityBlocks.map(renderPriorityBlock)}
              </div>

              {/* Legend */}
              <div className={styles.legendBar}>
                <div className={styles.legendItem}><span className={styles.legendDot} style={{ background: '#25c26e' }}/>En cours</div>
                <div className={styles.legendItem}><span className={styles.legendDot} style={{ background: '#60a5fa' }}/>Heure planifiée</div>
                <div className={styles.legendItem}><span className={styles.legendDot} style={{ background: '#2e6fe0' }}/>Publicité activée</div>
                <div className={styles.legendItem}><span className={styles.legendDot} style={{ background: '#a78bfa' }}/>Bandeau affiché</div>
                <div className={styles.legendItem}><Icons.Repeat/>En boucle — la suite est suspendue</div>
                <div style={{ marginLeft: 'auto', fontSize: '0.72rem', color: 'var(--tv-text-3, #6e7681)' }}>
                  Glissez-déposez pour réorganiser l'ordre de diffusion.
                </div>
              </div>
            </div>
          </div>

        </div>{/* fin .bodyLeft */}

        {/* ── Colonne droite : Sidebar ── */}
        <div className={styles.sidebar}>
          <div className={styles.sidebarCard}>
            <p className={styles.sidebarTitle}><Icons.Cal/>Résumé de la journée</p>
            <div className={styles.sidebarStatRow}>
<div className={styles.sidebarStatIcon} style={{ background: 'rgba(27,68,103)', color: '#60a5fa' }}><Icon name="calendarAdd" width= {28} height = {28} /></div>
              <div className={styles.sidebarStatBody}>
                <div className={styles.sidebarStatLabel}>Vidéos programmées</div>
                <div className={styles.sidebarStatValue}>{scheduledCount}</div>
              </div>
            </div>
            <div className={styles.sidebarStatRow}>
              <div className={styles.sidebarStatIcon} style={{ background: 'rgba(45,43,127)', color: '#25c26e' }}><Icon name="timeDuration" width = {28} height = {28}/></div>
              <div className={styles.sidebarStatBody}>
                <div className={styles.sidebarStatLabel}>Durée totale</div>
                <div className={styles.sidebarStatValue}>{loopHolder !== -1 ? '∞' : formatDurationInSeconds(totalSec)}</div>
              </div>
            </div>
            <div className={styles.sidebarStatRow}>
              <div className={styles.sidebarStatIcon} style={{ background: 'rgba(40,99,48)', color: '#a78bfa' }}><Icon name="playSymbol" width = {28} height = {28}/></div>
              <div className={styles.sidebarStatBody}>
                <div className={styles.sidebarStatLabel}>Prochaine diffusion</div>
                <div className={styles.sidebarStatValue}>{nextTime !== '—' ? `Aujourd'hui à ${nextTime}` : '—'}</div>
              </div>
            </div>
            <div className={styles.sidebarIllustration}>
              <div className={styles.scheduleImg}>
                <img src="/images/schedule.png" alt="Planning"/>
              </div>
              <p className={styles.sidebarIllustrationText}>Tout est prêt pour une belle journée de diffusion&nbsp;!</p>
            </div>
          </div>
        </div>

      </div>{/* fin .bodyGrid */}

    </div>
  );
};

export default BroadcastedMediaListV2;